/* eslint-disable max-len */
import React, { useState, useEffect, useLayoutEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';
import { Div, Button } from '@pinpoint/design-kit';
import Wrapper from 'src/Wrapper';
import { traitsPropType, stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';
import { Palette } from 'src/components';
import themes from 'src/themes';

/**
 * NOTE: Though the Radar component
 * accepts a theme the theme attribute
 * passed in the series takes higher
 * precedence
 * */

function Radar({
  title,
  labels,
  series,
  style,
  options,
  formatter,
  showRange: showRangeProp,
  onMouseover,
  showRangeControl: showRangeControlProp,
  showRangeCallback,
  showPalette: showPaletteProp,
  theme: themeProp,
  relative,
  onThemeChange,
  controls,
  defaultTheme,
  showTooltip,
}) {
  const [showRange, setShowRange] = useState(series.length < 2 ? showRangeProp : false);
  const [showRangeControl, setShowRangeControl] = useState(series.length < 2 ? showRangeControlProp : false);
  const [showControl, setShowControl] = useState(
    controls.length > 0 || (series.length < 2 ? showRangeControlProp : false)
  );
  const [theme, setTheme] = useState(themeProp);
  const [showPalette, setShowPalette] = useState(showPaletteProp !== null ? showPaletteProp : series.length < 2);

  useEffect(() => {
    setTheme(themeProp);
  }, [themeProp]);

  useEffect(() => {
    setShowRange(series.length < 2 ? showRangeProp : false);
  }, [series, showRangeProp]);

  useEffect(() => {
    setShowRangeControl(series.length < 2 ? showRangeControlProp : false);
    setShowControl(controls.length > 0 || (series.length < 2 ? showRangeControlProp : false));
  }, [series, showRangeControlProp, controls]);

  useEffect(() => {
    setShowPalette(showPaletteProp !== null ? showPaletteProp : series.length < 2);
  }, [series]);

  const minTraitAxisValue = 0;
  const minRangeAxisValue = 0;
  let maxTraitAxisValue = 100;
  let maxRangeAxisValue = 100;
  if (relative) {
    const maxValue = series.reduce((currentMax, { values }) => {
      const localMax = Math.max(...values);
      return localMax > currentMax ? localMax : currentMax;
    }, 0);
    const rangeMaxValue = series.reduce((currentMax, { values }) => {
      const localMax = Math.max(...values.map((value) => 50 + Math.abs(50 - value)));
      return localMax > currentMax ? localMax : currentMax;
    }, 0);
    maxTraitAxisValue = maxValue % 10 === 0 ? maxValue : Math.floor(maxValue / 10) * 10 + 10;
    maxRangeAxisValue = rangeMaxValue % 10 === 0 ? rangeMaxValue : Math.floor(rangeMaxValue / 10) * 10 + 10;
  }

  // Find the min value and the max value
  const seriesList = [];
  for (let i = 0; i < series.length; i += 1) {
    seriesList.push({
      name: series[i].name,
      type: 'radar',
      ...style.Radar.series,
      label: {
        ...options.label,
        ...style.Radar.label,
        formatter,
        distance: series[i].values.length > 40 ? 18 : 5,
      },
      itemStyle: {
        ...style.Radar.itemStyle,
        color: series[i].theme && themes[series[i].theme] ? themes[series[i].theme].color[0] : undefined,
      },
      lineStyle: {
        ...style.Radar.lineStyle,
      },
      areaStyle: {
        ...style.Radar.areaStyle,
      },
      data: [
        {
          name: series[i].name,
          value: showRange
            ? series[i].values.map((x) => 50 + Math.abs(50 - x)).reverse()
            : series[i].values
                .map((x) => x) // makes a new copy before reversing
                .reverse(),
        },
      ],
    });
  }
  const chartOption = {
    title: {
      text: title,
      ...style.global.title,
    },
    textStyle: {
      ...style.Radar.textStyle,
    },
    backgroundColor: style.global.backgroundColor,
    toolbox: {
      ...style.global.toolbox,
    },
    legend: {
      ...style.Radar.legend,
      data: series.map(({ name }) => name),
      show: series.length > 1,
    },
    tooltip: {
      ...style.global.tooltip,
      textStyle: {
        ...style.global.tooltip.textStyle,
      },
      padding: 16,
      trigger: 'item',
      show: showTooltip,
      formatter: (params) => {
        const { value } = params;
        const toolTipArray = value.map(
          (v, i) => `${v.toFixed(1)} percentile of ${
            showRange
              ? series[0].values[value.length - 1 - i] > 50
                ? labels[labels.length - 1 - i].highRangeName
                : labels[labels.length - 1 - i].lowRangeName
              : labels[labels.length - 1 - i].trait
          }
          `
        );
        toolTipArray.reverse();
        const toolTipText = toolTipArray.slice(0, 7).join('<br />');
        return `The median score is in the <br/> ${toolTipText}`;
      },
    },
    radar: {
      ...style.Radar.radar,
      startAngle: style.Radar.startAngle(labels),
      axisLine: {
        ...style.Radar.axisLine,
      },
      axisLabel: {
        ...style.Radar.axisLabel,
      },
      splitNumber: showRange ? maxRangeAxisValue / 10 : maxTraitAxisValue / 10,
      splitLine: {
        ...style.Radar.splitLine,
      },
      splitArea: {
        ...style.Radar.splitArea,
      },
      indicator: labels
        .map((label, index) => ({
          name: showRange ? (series[0].values[index] < 50 ? label.lowRangeName : label.highRangeName) : label.trait,
          min: showRange ? minRangeAxisValue : minTraitAxisValue,
          max: showRange ? maxRangeAxisValue : maxTraitAxisValue,
        }))
        .reverse(),
    },
    series: seriesList,
  };
  const mergedOptions = merge(chartOption, options);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <Wrapper>
      {showControl ? (
        <Div
          d="flex"
          align="center"
          justify="flex-end"
          h="2.5rem"
          m={{ x: '-0.4rem', b: '0rem' }}
          style={{
            backgroundColor: mergedOptions.backgroundColor,
          }}
        >
          {controls.length > 0 ? React.Children.toArray(controls) : null}
          {showRangeControl ? (
            <Button
              bg="brand500"
              hoverBg="brand600"
              onClick={() => {
                if (![undefined, null].includes(showRangeCallback)) showRangeCallback((value) => !value);
                else setShowRange((value) => !value);
              }}
              m={{ x: '0.5rem' }}
              data-tip={!showRange ? 'View traits as adjectives' : 'View trait names'}
            >
              {showRange ? 'SHOW TRAITS' : 'SHOW RANGES'}
            </Button>
          ) : null}
        </Div>
      ) : null}
      <ReactEcharts
        ref={(element) => {
          const instance = element ? element.getEchartsInstance() : null;
          if (instance) {
            instance.off('mouseover');
            instance.on('mouseover', 'radar', ({ name }) => {
              const index = labels.findIndex(
                ({ trait, lowRangeName, highRangeName }) =>
                  trait === name || lowRangeName === name || highRangeName === name
              );
              const label = labels[index];
              const values = series.map((_series) => _series.values[index]);
              onMouseover({ label, values });
            });
          }
        }}
        style={{
          width: '100%',
          minHeight: '20vh',
          height: `calc(100% - ${showControl ? '1.5rem' : '0rem'} - ${showPalette ? '3.5rem' : '0rem'})`,
          overflow: 'visible',
        }}
        option={mergedOptions}
        theme={theme}
        notMerge
      />
      {showPalette ? (
        <Palette
          defaultTheme={defaultTheme}
          selected={theme}
          onSelect={(newTheme) => {
            setTheme(newTheme);
            onThemeChange(newTheme);
          }}
          style={{
            backgroundColor: mergedOptions.backgroundColor,
            width: '10rem',
            margin: '0 auto',
            paddingTop: '1rem',
          }}
        />
      ) : null}
    </Wrapper>
  );
}

Radar.propTypes = {
  labels: traitsPropType.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.number),
    })
  ).isRequired,
  title: PropTypes.string,
  style: stylePropType,
  options: PropTypes.shape({}),
  showTooltip: PropTypes.bool,
  formatter: PropTypes.func,
  showRange: PropTypes.bool,
  onMouseover: PropTypes.func,
  showRangeControl: PropTypes.bool,
  showRangeCallback: PropTypes.func,
  showPalette: PropTypes.bool,
  theme: PropTypes.string,
  relative: PropTypes.bool,
  onThemeChange: PropTypes.func,
  controls: PropTypes.arrayOf(PropTypes.node),
};

Radar.defaultProps = {
  title: '',
  style: Style1,
  options: {},
  formatter: (params) => params.value.toFixed(1),
  showRange: false,
  onMouseover: () => null,
  showRangeControl: false,
  showRangeCallback: null,
  showPalette: null,
  theme: 'pinpoint',
  relative: false,
  onThemeChange: () => null,
  controls: [],
  showTooltip: true,
};

export default Radar;
